import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Chip,
  Box,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Skeleton
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon
} from '@mui/icons-material';

const DataTable = ({
  title,
  data = [],
  columns = [],
  loading = false,
  onRowClick,
  onEdit,
  onDelete,
  onView,
  maxHeight = 400,
  dense = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 5 : 10);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatCellValue = (value, column) => {
    if (value === null || value === undefined) return '-';
    
    switch (column.type) {
      case 'currency':
        return `$${Number(value).toLocaleString('he-IL')}`;
      case 'number':
        return Number(value).toLocaleString('he-IL');
      case 'date':
        return new Date(value).toLocaleDateString('he-IL');
      case 'datetime':
        return new Date(value).toLocaleString('he-IL');
      case 'status':
        const statusColors = {
          'active': 'success',
          'פעיל': 'success',
          'inactive': 'default',
          'לא פעיל': 'default',
          'pending': 'warning',
          'ממתין': 'warning',
          'error': 'error',
          'שגיאה': 'error',
          'completed': 'info',
          'הושלם': 'info'
        };
        return (
          <Chip
            label={value}
            color={statusColors[value] || 'default'}
            size="small"
            variant="outlined"
          />
        );
      case 'boolean':
        return (
          <Chip
            label={value ? 'כן' : 'לא'}
            color={value ? 'success' : 'default'}
            size="small"
            variant="outlined"
          />
        );
      default:
        return String(value);
    }
  };

  const renderActions = (row, index) => {
    if (!onView && !onEdit && !onDelete) return null;

    return (
      <TableCell align="left" sx={{ minWidth: 120 }}>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {onView && (
            <Tooltip title="צפייה">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onView(row, index);
                }}
              >
                <ViewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {onEdit && (
            <Tooltip title="עריכה">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(row, index);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {onDelete && (
            <Tooltip title="מחיקה">
              <IconButton
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(row, index);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </TableCell>
    );
  };

  const renderSkeletonRows = () => (
    Array.from({ length: rowsPerPage }).map((_, index) => (
      <TableRow key={index}>
        {columns.map((column, colIndex) => (
          <TableCell key={colIndex}>
            <Skeleton variant="text" />
          </TableCell>
        ))}
        {(onView || onEdit || onDelete) && (
          <TableCell>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Skeleton variant="circular" width={32} height={32} />
              <Skeleton variant="circular" width={32} height={32} />
            </Box>
          </TableCell>
        )}
      </TableRow>
    ))
  );

  const paginatedData = data.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {title && (
        <CardHeader
          title={
            <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              {title}
            </Typography>
          }
          sx={{ pb: 1 }}
        />
      )}
      <CardContent sx={{ flex: 1, pt: title ? 0 : 2, p: 0 }}>
        <TableContainer sx={{ maxHeight, overflow: 'auto' }}>
          <Table 
            size={dense || isMobile ? 'small' : 'medium'}
            stickyHeader
          >
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.field}
                    align={column.align || 'right'}
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: 'background.paper',
                      minWidth: column.minWidth || 'auto',
                      display: isMobile && column.hideOnMobile ? 'none' : 'table-cell'
                    }}
                  >
                    {column.headerName || column.field}
                  </TableCell>
                ))}
                {(onView || onEdit || onDelete) && (
                  <TableCell
                    align="left"
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: 'background.paper',
                      minWidth: 120
                    }}
                  >
                    פעולות
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                renderSkeletonRows()
              ) : paginatedData.length > 0 ? (
                paginatedData.map((row, index) => (
                  <TableRow
                    key={row.id || index}
                    hover
                    onClick={onRowClick ? () => onRowClick(row, index) : undefined}
                    sx={{
                      cursor: onRowClick ? 'pointer' : 'default',
                      '&:hover': onRowClick ? { backgroundColor: 'action.hover' } : {}
                    }}
                  >
                    {columns.map((column) => (
                      <TableCell
                        key={column.field}
                        align={column.align || 'right'}
                        sx={{
                          display: isMobile && column.hideOnMobile ? 'none' : 'table-cell'
                        }}
                      >
                        {formatCellValue(row[column.field], column)}
                      </TableCell>
                    ))}
                    {renderActions(row, index)}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell 
                    colSpan={columns.length + (onView || onEdit || onDelete ? 1 : 0)}
                    align="center"
                    sx={{ py: 4 }}
                  >
                    <Typography color="text.secondary">
                      אין נתונים להצגה
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {data.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={data.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="שורות בעמוד:"
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} מתוך ${count !== -1 ? count : `יותר מ-${to}`}`
            }
            sx={{
              borderTop: 1,
              borderColor: 'divider',
              '& .MuiTablePagination-toolbar': {
                direction: 'ltr'
              }
            }}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default DataTable;
